import { Typography } from '../primitives/index.js'
import { logger } from '@tunasong/models'
import type { Persisted, Entity } from '@tunasong/schemas'
import { useCallback, useEffect, useRef } from 'react'
import type { CSSProperties, FC, HTMLAttributes } from 'react'
import { useDropzone } from 'react-dropzone'
import type { Accept, FileWithPath } from 'react-dropzone'
import { isMobileDevice } from '../browser/index.js'
import { useStableRef } from '../hooks/stable-ref.js'
import { useUploadFiles } from '../upload/upload-files.hook.js'
import { LinearProgress } from '../primitives/index.js'

export interface DropzoneProps extends HTMLAttributes<HTMLDivElement> {
  className?: string
  parentId: string | null
  accept: Accept
  tags?: string[]
  contentHint?: string
  showZone?: boolean
  showClickZone?: boolean
  isPrivate?: boolean
  uploadOnClick?: boolean
  maxFiles?: number
  onUploading?(): void
  onUploaded?(uploaded: Persisted<Entity>): void
}

export const DropZone: FC<DropzoneProps> = props => {
  const {
    className,
    parentId,
    accept,
    children,
    showZone = false,
    uploadOnClick = false,
    isPrivate = false,
    contentHint = 'file',
    maxFiles = 50,
    title,
    showClickZone,
    onUploaded,
    onUploading,
    tags,
    ...restProps
  } = props

  const { uploadFiles, uploading, handleChange } = useUploadFiles({ isPrivate, onUploaded, tags })
  const onUploadingRef = useStableRef(onUploading)
  useEffect(() => {
    if (uploading && onUploadingRef.current) {
      onUploadingRef.current()
    }
  }, [onUploadingRef, uploading])
  const onDrop = useCallback(
    async (acceptedFiles: FileWithPath[]) => {
      uploadFiles({ files: acceptedFiles, parentId }).catch(err => {
        logger.error('Failed to upload files', err)
      })
    },
    [parentId, uploadFiles]
  )
  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    maxFiles,
    accept,
    noClick: true,
  })
  const uploadRef = useRef<HTMLInputElement | null>(null)
  const handleClick = useCallback(() => {
    if (showZone || uploadOnClick) {
      uploadRef.current?.click()
    }
  }, [showZone, uploadOnClick])
  const { ...rootProps } = getRootProps()
  const acceptStr =
    Object.keys(accept).join(',') +
    Object.values(accept)
      .flatMap(v => v)
      .join(',')
  const dropZoneText = title ?? (isMobileDevice() ? 'Tap to upload' : 'Drop files or click to upload')
  const isActive = isDragActive

  const baseStyle: CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 100,
    outline: 'none',
    marginBottom: 16,
    ...(showZone && {
      borderColor: 'var(--tuna-palette-divider)',
      borderWidth: 2,
      borderStyle: 'dashed',
    }),
    ...(isActive && {
      marginTop: 8,
      justifyContent: 'center',
      padding: 16,
      borderColor: 'var(--tuna-palette-primary-main)',
      ...(isDragReject && { borderColor: 'var(--tuna-palette-divider)' }),
      ...(isDragAccept && {
        borderColor: 'var(--tuna-palette-success-main)',
        color: 'var(--tuna-palette-success-main)',
      }),
    }),
  }

  return (
    <>
      <div
        {...rootProps}
        className={className}
        style={baseStyle}
        tabIndex={-1}
        title={uploadOnClick ? dropZoneText : undefined}
        onClick={uploadOnClick && !showClickZone ? handleClick : undefined}
        {...restProps}
      >
        <input
          {...getInputProps()}
          ref={uploadRef}
          disabled={uploading}
          type="file"
          multiple
          accept={acceptStr}
          hidden
          onChange={handleChange(parentId)}
        />
        <Typography
          variant="caption"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignContent: 'center',
            color: 'var(--tuna-palette-text-secondary)',
          }}
        >
          {isDragAccept && `All file(s) accepted. Drop to add ${contentHint}`}
          {isDragReject && 'Some files may be rejected'}
        </Typography>
        {!isDragActive ? <div>{children}</div> : null}
        {uploadOnClick && showClickZone && !isDragActive ? (
          <div
            onClick={handleClick}
            style={{
              display: 'flex',
              color: 'var(--tuna-palette-action-disabled)',
              flex: 1,
              cursor: 'pointer',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Typography variant="caption">{dropZoneText}</Typography>
          </div>
        ) : null}
      </div>
      {uploading ? <LinearProgress /> : null}
    </>
  )
}

export default DropZone
